import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGraduationCap,
    faMagnifyingGlass,
    faEarthAmericas,
    faSquareList,
    faUserRobot,
} from '@fortawesome/pro-solid-svg-icons';
import { AI_ADVISOR_BRAND_NAME, hasBotPageAccess as checkBotPageAccess } from 'TutorBotAiAdvisorChat';
import { type CurrentUser, getUserProgramState } from 'Users';
import { getProgramTypeConfig } from 'Program';
import { useAppHeader } from '../../hooks';

// Navigation item type for the mobile bottom nav
type NavItem = {
    key: string;
    label: string;
    icon: typeof faGraduationCap;
    route: string;
    disabled?: boolean;
    show: boolean;
};

type NavItemProps = {
    item: NavItem;
    isActive: boolean;
    onClick: () => void;
    setRef: (el: HTMLDivElement | null) => void;
};

function NavItemButton({ item, isActive, onClick, setRef }: NavItemProps) {
    return (
        <div
            ref={setRef}
            className={clsx(
                'flex',
                'flex-1',
                'flex-col',
                'items-center',
                'justify-center',
                'h-full',
                item.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                'transition-colors',
                'duration-200',
                item.disabled && 'opacity-40',
            )}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') onClick();
            }}
        >
            <FontAwesomeIcon
                icon={item.icon}
                className={clsx('text-[22px]', 'mb-1', isActive ? 'text-primary' : 'text-slate-grey')}
            />
            <span
                className={clsx(
                    'text-[11px]',
                    'font-normal',
                    'leading-tight',
                    isActive ? 'text-primary' : 'text-slate-grey',
                )}
            >
                {item.label}
            </span>
        </div>
    );
}

function useNavItems() {
    const { t } = useTranslation();
    const { inOfflineMode, currentUser } = useAppHeader();
    const typedCurrentUser = currentUser as CurrentUser | null;

    const hasStudentNetworkAccess = typedCurrentUser?.hasStudentNetworkAccess ?? false;
    const programTypeConfig = getProgramTypeConfig(getUserProgramState(typedCurrentUser)?.programType);
    const supportsResourcesTab = !!programTypeConfig?.supportsResourcesTab;
    const hasBotPageAccess = typedCurrentUser ? checkBotPageAccess({ currentUser: typedCurrentUser }) : false;

    return useMemo(() => {
        const items: NavItem[] = [
            {
                key: 'home',
                label: t('navigation.app_menu.home'),
                icon: faGraduationCap,
                route: '/dashboard',
                show: true,
            },
            {
                key: 'courses',
                label: t('navigation.app_menu.courses'),
                icon: faMagnifyingGlass,
                route: '/courses',
                disabled: inOfflineMode,
                show: true,
            },
            {
                key: 'ai-advisor',
                label: AI_ADVISOR_BRAND_NAME,
                icon: faUserRobot,
                route: '/bot',
                disabled: inOfflineMode,
                show: hasBotPageAccess,
            },
            {
                key: 'student-network',
                label: t('navigation.app_menu.student_network'),
                icon: faEarthAmericas,
                route: '/student-network',
                disabled: inOfflineMode,
                show: hasStudentNetworkAccess,
            },
            {
                key: 'resources',
                label: t('navigation.app_menu.resources'),
                icon: faSquareList,
                route: '/resources',
                disabled: inOfflineMode,
                show: supportsResourcesTab,
            },
        ];
        return items.filter(item => item.show);
    }, [t, inOfflineMode, hasBotPageAccess, hasStudentNetworkAccess, supportsResourcesTab]);
}

function useSlidingIndicator(activeNavKey: string | null, navContainerRef: React.RefObject<HTMLDivElement | null>) {
    const navItemRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);
    const prevActiveKeyRef = useRef<string | null>(null);

    const updateIndicatorPosition = useCallback(() => {
        if (!activeNavKey || !navContainerRef.current) return;
        const activeElement = navItemRefs.current.get(activeNavKey);
        if (!activeElement) return;
        setIndicatorStyle({ left: activeElement.offsetLeft, width: activeElement.offsetWidth });
    }, [activeNavKey, navContainerRef]);

    useEffect(() => {
        if (prevActiveKeyRef.current === activeNavKey) return undefined;
        const isInitialMount = prevActiveKeyRef.current === null;
        prevActiveKeyRef.current = activeNavKey;

        if (isInitialMount) {
            updateIndicatorPosition();
            return undefined;
        }

        const timeoutId = setTimeout(updateIndicatorPosition, 50);
        return () => clearTimeout(timeoutId);
    }, [activeNavKey, updateIndicatorPosition]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateIndicatorPosition, 200);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [updateIndicatorPosition]);

    const setNavItemRef = useCallback(
        (key: string) => (el: HTMLDivElement | null) => {
            navItemRefs.current.set(key, el);
        },
        [],
    );

    return { indicatorStyle, setNavItemRef };
}

export function MobileBottomNav() {
    const { layout, hideMobileMenu, loadRoute, isActive } = useAppHeader();

    const navContainerRef = useRef<HTMLDivElement>(null);
    const navItems = useNavItems();

    const activeNavKey = useMemo(() => {
        const activeItem = navItems.find(item => isActive([item.route]));
        return activeItem?.key ?? null;
    }, [navItems, isActive]);

    const { indicatorStyle, setNavItemRef } = useSlidingIndicator(activeNavKey, navContainerRef);

    const handleNavClick = useCallback(
        (item: NavItem) => {
            if (item.disabled) return;
            loadRoute(item.route);
        },
        [loadRoute],
    );

    if (layout !== 'default' || hideMobileMenu) return null;

    return (
        <nav
            ref={navContainerRef}
            className={clsx(
                'mobile-bottom-nav',
                'fixed',
                'bottom-0',
                'left-0',
                'right-0',
                'h-[55px]', // $APP_MENU_MOBILE_HEIGHT
                'notch:mb-[env(safe-area-inset-bottom)]',
                'bg-white',
                'border-0',
                'border-t',
                'border-solid',
                'border-slate-grey-200',
                'z-[99]',
                'transform-gpu', // Force GPU compositing to prevent mobile Safari repaint flicker during address bar animation
                'sm:hidden',
                // White background extension into safe area below
                'after:content-[""]',
                'after:absolute',
                'after:top-full',
                'after:left-0',
                'after:right-0',
                'notch:after:h-[env(safe-area-inset-bottom)]',
                'after:bg-white',
            )}
        >
            <div
                className={clsx(
                    'absolute',
                    '-top-px',
                    'z-[1]',
                    'h-[3px]',
                    'bg-primary',
                    'rounded-b-full',
                    'transition-all',
                    'duration-300',
                    'ease-in-out',
                    (!indicatorStyle || !activeNavKey) && 'opacity-0',
                )}
                style={{ left: indicatorStyle?.left ?? 0, width: indicatorStyle?.width ?? 0 }}
            />
            <div className="flex h-full w-full items-center justify-around notch:pt-1">
                {navItems.map(item => (
                    <NavItemButton
                        key={item.key}
                        item={item}
                        isActive={activeNavKey === item.key}
                        onClick={() => handleNavClick(item)}
                        setRef={setNavItemRef(item.key)}
                    />
                ))}
            </div>
        </nav>
    );
}
